import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
  FaRegClock,
  FaRegStar,
  FaStar,
} from "react-icons/fa";
import { FiSearch, FiX } from "react-icons/fi";
import { NAV_MAX_DEPTH, flattenNavLeaves, isNavSection, matchesNavQuery } from "../utils/navTree";
import { MAX_RECENT_NAV_ITEMS, getPinnedNavIds, togglePinnedNavId } from "../utils/navPrefs";
import "./Sidebar.css";

const collectSubtreeIds = (item, out = []) => {
  out.push(item.id);
  (item.children || []).forEach((child) => collectSubtreeIds(child, out));
  return out;
};

const indexNodes = (items, map = new Map()) => {
  items.forEach((item) => {
    map.set(item.id, item);
    indexNodes(item.children || [], map);
  });
  return map;
};

function Sidebar({
  onNavigate,
  collapsed,
  onToggle,
  mobileOpen,
  onMobileClose,
  items,
  activeTrail = [],
  recentIds = [],
  employeeId,
}) {
  const [filter, setFilter] = useState("");
  const [openIds, setOpenIds] = useState(() => new Set());
  const [flyout, setFlyout] = useState(null);
  const [pinnedIds, setPinnedIds] = useState(() => getPinnedNavIds(employeeId));
  const closeTimer = useRef(null);

  // The rail only behaves as a rail on desktop — on mobile the drawer is always
  // full width even when the desktop toggle happens to be collapsed.
  const railMode = collapsed && !mobileOpen;

  const leaves = useMemo(() => flattenNavLeaves(items), [items]);
  const leafById = useMemo(() => new Map(leaves.map((leaf) => [leaf.id, leaf])), [leaves]);
  const nodeById = useMemo(() => indexNodes(items), [items]);

  const currentId = activeTrail[activeTrail.length - 1] || null;
  const trailSet = useMemo(() => new Set(activeTrail), [activeTrail]);
  const trailKey = activeTrail.join(">");

  useEffect(() => {
    setPinnedIds(getPinnedNavIds(employeeId));
  }, [employeeId]);

  // Navigating opens the branch you landed in and closes the rest, so the rail
  // always reflects where you are rather than accumulating open groups.
  useEffect(() => {
    setOpenIds(new Set(trailKey ? trailKey.split(">").slice(0, -1) : []));
  }, [trailKey]);

  useEffect(() => {
    if (!railMode) setFlyout(null);
  }, [railMode]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const go = useCallback(
    (route) => {
      if (!route) return;
      onNavigate(route);
      setFilter("");
      setFlyout(null);
      if (mobileOpen) onMobileClose();
    },
    [mobileOpen, onMobileClose, onNavigate]
  );

  const toggleNode = useCallback((item, siblings) => {
    setOpenIds((previous) => {
      const next = new Set(previous);
      if (next.has(item.id)) {
        collectSubtreeIds(item).forEach((id) => next.delete(id));
        return next;
      }
      siblings.forEach((sibling) => {
        if (sibling.id !== item.id) {
          collectSubtreeIds(sibling).forEach((id) => next.delete(id));
        }
      });
      next.add(item.id);
      return next;
    });
  }, []);

  const handlePin = (event, itemId) => {
    event.stopPropagation();
    setPinnedIds(togglePinnedNavId(employeeId, itemId));
  };

  const openFlyout = (item, event) => {
    clearTimeout(closeTimer.current);
    const { top } = event.currentTarget.getBoundingClientRect();
    setFlyout({ id: item.id, top });
  };

  const scheduleCloseFlyout = () => {
    closeTimer.current = setTimeout(() => setFlyout(null), 150);
  };

  const pinnedLeaves = useMemo(
    () => pinnedIds.map((id) => leafById.get(id)).filter(Boolean),
    [pinnedIds, leafById]
  );

  const recentLeaves = useMemo(
    () =>
      recentIds
        .map((id) => leafById.get(id))
        .filter(Boolean)
        .filter((leaf) => leaf.id !== currentId && !pinnedIds.includes(leaf.id))
        .slice(0, MAX_RECENT_NAV_ITEMS),
    [recentIds, leafById, currentId, pinnedIds]
  );

  const filterQuery = filter.trim();
  const filterResults = useMemo(
    () => (filterQuery ? leaves.filter((leaf) => matchesNavQuery(leaf, filterQuery)) : []),
    [filterQuery, leaves]
  );

  const renderPinButton = (item) => {
    const pinned = pinnedIds.includes(item.id);
    const label = pinned ? `Unpin ${item.label}` : `Pin ${item.label}`;
    return (
      <button
        type="button"
        className={`sidebar-pin ${pinned ? "pinned" : ""}`}
        onClick={(event) => handlePin(event, item.id)}
        title={label}
        aria-label={label}
      >
        {pinned ? <FaStar /> : <FaRegStar />}
      </button>
    );
  };

  const renderQuickRow = (leaf, { withTrail = false, keyPrefix = "" } = {}) => {
    const Icon = leaf.icon;
    return (
      <div
        key={`${keyPrefix}${leaf.id}`}
        className={`sidebar-row quick ${currentId === leaf.id ? "active" : ""}`}
      >
        <button
          type="button"
          className="sidebar-row-main"
          onClick={() => go(leaf.route)}
          title={leaf.description || undefined}
          aria-current={currentId === leaf.id ? "page" : undefined}
        >
          <span className="sidebar-icon">
            <Icon />
          </span>
          <span className="sidebar-label">
            <span className="sidebar-row-label">{leaf.label}</span>
            {withTrail && leaf.trailLabels.length > 0 && (
              <span className="sidebar-row-trail">{leaf.trailLabels.join(" / ")}</span>
            )}
          </span>
        </button>
        {renderPinButton(leaf)}
      </div>
    );
  };

  const renderNode = (item, depth, siblings) => {
    const Icon = item.icon;
    const children = item.children || [];
    // Rows deeper than the cap aren't rendered — a hub at the cap keeps its own
    // route, so its subtree is reached from its page instead.
    const expandable = !railMode && children.length > 0 && depth < NAV_MAX_DEPTH - 1;
    const expanded = expandable && openIds.has(item.id);
    const isCurrent = currentId === item.id;
    const onTrail = !isCurrent && trailSet.has(item.id);
    const railTop = railMode && depth === 0;

    return (
      <div
        key={item.id}
        className={`sidebar-node depth-${depth}`}
        onMouseEnter={railTop ? (event) => openFlyout(item, event) : undefined}
        onMouseLeave={railTop ? scheduleCloseFlyout : undefined}
      >
        <div
          className={`sidebar-row ${isCurrent ? "active" : ""} ${onTrail ? "on-trail" : ""} ${
            expanded ? "expanded" : ""
          }`}
        >
          <button
            type="button"
            className="sidebar-row-main"
            onClick={() => {
              if (item.route) go(item.route);
              else if (expandable) toggleNode(item, siblings);
            }}
            title={railMode ? item.label : item.description || undefined}
            aria-current={isCurrent ? "page" : undefined}
          >
            <span className="sidebar-icon">
              <Icon />
            </span>
            <span className="sidebar-label">{item.label}</span>
          </button>

          {!railMode && item.route && renderPinButton(item)}

          {expandable && (
            <button
              type="button"
              className="sidebar-chevron"
              onClick={() => toggleNode(item, siblings)}
              aria-expanded={expanded}
              aria-label={`${expanded ? "Collapse" : "Expand"} ${item.label}`}
            >
              {expanded ? <FaChevronUp /> : <FaChevronDown />}
            </button>
          )}
        </div>

        {expanded && (
          <div className="sidebar-children">
            {children.map((child) => renderNode(child, depth + 1, children))}
          </div>
        )}
      </div>
    );
  };

  const renderTopLevel = (item) => {
    if (!isNavSection(item)) return renderNode(item, 0, items);
    return (
      <div key={item.id} className="sidebar-section">
        {railMode ? (
          <div className="sidebar-section-rule" />
        ) : (
          <div className="sidebar-section-caption">{item.label}</div>
        )}
        {item.children.map((child) => renderNode(child, 0, item.children))}
      </div>
    );
  };

  const flyoutItem = flyout ? nodeById.get(flyout.id) : null;

  return (
    <>
      {mobileOpen && <div className="sidebar-overlay" onClick={onMobileClose} />}

      <aside className={`sidebar ${collapsed ? "collapsed" : ""} ${mobileOpen ? "mobile-open" : ""}`}>
        <div className="sidebar-brand">
          <Link to="/" className="brand-link">
            <img src="/assets/Logo.jpg" alt="Heel logo" className="sidebar-logo" />
            <span className="sidebar-app-title">The Heel Portal</span>
          </Link>
        </div>

        {!railMode && (
          <div className="sidebar-filter">
            <FiSearch className="sidebar-filter-icon" />
            <input
              type="text"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") setFilter("");
              }}
              placeholder="Filter navigation"
              aria-label="Filter navigation"
            />
            {filter && (
              <button
                type="button"
                className="sidebar-filter-clear"
                onClick={() => setFilter("")}
                aria-label="Clear filter"
              >
                <FiX />
              </button>
            )}
          </div>
        )}

        <nav className="sidebar-nav">
          {filterQuery ? (
            <div className="sidebar-section">
              <div className="sidebar-section-caption">
                {filterResults.length} {filterResults.length === 1 ? "match" : "matches"}
              </div>
              {filterResults.map((leaf) =>
                renderQuickRow(leaf, { withTrail: true, keyPrefix: "filter-" })
              )}
              {filterResults.length === 0 && (
                <div className="sidebar-empty">Nothing matches "{filterQuery}"</div>
              )}
            </div>
          ) : (
            <>
              {!railMode && pinnedLeaves.length > 0 && (
                <div className="sidebar-section">
                  <div className="sidebar-section-caption">
                    <FaStar className="sidebar-caption-icon" />
                    Pinned
                  </div>
                  {pinnedLeaves.map((leaf) => renderQuickRow(leaf, { keyPrefix: "pin-" }))}
                </div>
              )}

              {!railMode && recentLeaves.length > 0 && (
                <div className="sidebar-section">
                  <div className="sidebar-section-caption">
                    <FaRegClock className="sidebar-caption-icon" />
                    Recent
                  </div>
                  {recentLeaves.map((leaf) => renderQuickRow(leaf, { keyPrefix: "recent-" }))}
                </div>
              )}

              {items.map(renderTopLevel)}
            </>
          )}
        </nav>

        <button
          className="sidebar-toggle"
          onClick={onToggle}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </button>

        <div className="sidebar-footer">
          <div className="sidebar-divider" />
          <div className="sidebar-version">
            <span className="sidebar-label">v1.0</span>
          </div>
        </div>
      </aside>

      {flyoutItem &&
        createPortal(
          <div
            className="sidebar-flyout"
            style={{ top: Math.max(8, Math.min(flyout.top, window.innerHeight - 280)) }}
            onMouseEnter={() => clearTimeout(closeTimer.current)}
            onMouseLeave={() => setFlyout(null)}
          >
            <button
              type="button"
              className="sidebar-flyout-title"
              onClick={() => go(flyoutItem.route)}
              disabled={!flyoutItem.route}
            >
              {flyoutItem.label}
            </button>
            {(flyoutItem.children || []).length > 0 && (
              <div className="sidebar-flyout-list">
                {flyoutItem.children.map((child) => (
                  <React.Fragment key={child.id}>
                    <button
                      type="button"
                      className={`sidebar-flyout-row ${currentId === child.id ? "active" : ""}`}
                      onClick={() => go(child.route)}
                      disabled={!child.route}
                    >
                      <span className="sidebar-flyout-icon">
                        <child.icon />
                      </span>
                      {child.label}
                    </button>
                    {(child.children || []).map((grandchild) => (
                      <button
                        key={grandchild.id}
                        type="button"
                        className={`sidebar-flyout-row nested ${
                          currentId === grandchild.id ? "active" : ""
                        }`}
                        onClick={() => go(grandchild.route)}
                        disabled={!grandchild.route}
                      >
                        {grandchild.label}
                      </button>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
}

export default Sidebar;
